/**
 * Sealed escrow (Phase 16C C3, design §6.4, backend 16B B10).
 *
 * Every queued op is also sealed to the tenant's escrow public key, so documentation of care
 * survives a PIN shred, a revocation or a forced re-enrollment: the same user uploads the sealed
 * copies after the next online sign-in and the server opens them with the tenant private key.
 *
 *   ECDH P-256 (fresh ephemeral key per op)
 *   → HKDF-SHA256 (salt = opId as UTF-8, info = "pelora-escrow-v1") → 256-bit key
 *   → AES-256-GCM, 12-byte random IV, AAD = `${opId}:${tenantId}`
 *   sealed = { opId, kid, epk: SPKI base64, iv: base64, ct: base64 (ciphertext + tag) }
 *
 * A shared test vector (`__tests__/fixtures/escrow-vector.json`) pins the format for both repos.
 */
import type { EscrowKeyDto, SealedOpDto, SyncOpDto } from '../../dtos/offline/sync.dto';
import { fromBase64, randomBytes, toBase64 } from './crypto';

export const ESCROW_INFO = 'pelora-escrow-v1';
const encoder = new TextEncoder();
const decoder = new TextDecoder();
const EC = { name: 'ECDH', namedCurve: 'P-256' } as const;

function subtle(): SubtleCrypto {
  const s = globalThis.crypto?.subtle;
  if (!s) throw new Error('Web Crypto is not available on this device');
  return s;
}

async function deriveAesKey(privateKey: CryptoKey, publicKey: CryptoKey, opId: string, usage: KeyUsage): Promise<CryptoKey> {
  const shared = await subtle().deriveBits({ name: 'ECDH', public: publicKey }, privateKey, 256);
  const hkdf = await subtle().importKey('raw', shared, 'HKDF', false, ['deriveKey']);
  return subtle().deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: encoder.encode(opId), info: encoder.encode(ESCROW_INFO) },
    hkdf,
    { name: 'AES-GCM', length: 256 },
    false,
    [usage],
  );
}

export interface SealOptions {
  /** Fixed ephemeral key pair and IV: test vectors only. */
  ephemeral?: CryptoKeyPair;
  iv?: Uint8Array;
}

/** Seal raw bytes for one op id. */
export async function sealBytes(plaintext: Uint8Array, opId: string, tenantId: string, key: EscrowKeyDto, options: SealOptions = {}): Promise<SealedOpDto> {
  const recipient = await subtle().importKey('spki', fromBase64(key.spki), EC, false, []);
  const ephemeral = options.ephemeral || await subtle().generateKey(EC, false, ['deriveBits']) as CryptoKeyPair;
  const aes = await deriveAesKey(ephemeral.privateKey, recipient, opId, 'encrypt');
  const iv = options.iv || randomBytes(12);
  const ct = await subtle().encrypt({ name: 'AES-GCM', iv, additionalData: encoder.encode(`${opId}:${tenantId}`) }, aes, plaintext);
  const epk = await subtle().exportKey('spki', ephemeral.publicKey);
  return { opId, kid: key.kid, epk: toBase64(epk), iv: toBase64(iv), ct: toBase64(ct) };
}

/** Seal a wire op (its JSON) to the tenant escrow key. */
export function sealOp(op: SyncOpDto, key: EscrowKeyDto, options?: SealOptions): Promise<SealedOpDto> {
  return sealBytes(encoder.encode(JSON.stringify(op)), op.opId, op.tenantId, key, options);
}

/** Open a sealed op with the recipient private key (server side; used here by tests). */
export async function openSealed(sealed: SealedOpDto, recipientPrivateKey: CryptoKey, tenantId: string): Promise<string> {
  const epk = await subtle().importKey('spki', fromBase64(sealed.epk), EC, false, []);
  const aes = await deriveAesKey(recipientPrivateKey, epk, sealed.opId, 'decrypt');
  const plain = await subtle().decrypt(
    { name: 'AES-GCM', iv: fromBase64(sealed.iv), additionalData: encoder.encode(`${sealed.opId}:${tenantId}`) },
    aes,
    fromBase64(sealed.ct),
  );
  return decoder.decode(plain);
}
